import type {
    MoneyMapRuleWithAccount,
    MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';

import type { EditableRuleSet } from './editableRuleSetUtils';
import { UNGROUPED_GOALS_SET_ID } from './ruleSetIds';

export type EditableRule = EditableRuleSet['rules'][number];

/**
 * The editable goal sets flattened into one list, so a single draggable list
 * can move goals between sets. Monthly expenses isn't part of it (it isn't
 * reorderable), so pass only the goal sets to `flattenRuleSets`.
 */
export type FlatRow =
    | {
          kind: 'set-header';
          key: string;
          setId: string;
          ruleSet: EditableRuleSet;
          ordinal: number;
      }
    | {
          kind: 'goal';
          key: string;
          setId: string;
          ruleSet: EditableRuleSet;
          rule: EditableRule;
      }
    | {
          kind: 'set-footer';
          key: string;
          setId: string;
          ruleSet: EditableRuleSet;
          isLast: boolean;
      };

export const flattenRuleSets = (ruleSets: EditableRuleSet[]): FlatRow[] => {
    return ruleSets.flatMap((ruleSet, index): FlatRow[] => [
        {
            kind: 'set-header',
            key: `header:${ruleSet.id}`,
            setId: ruleSet.id,
            ruleSet,
            ordinal: index + 1,
        },
        ...ruleSet.rules.map(
            (rule): FlatRow => ({
                kind: 'goal',
                key: `goal:${rule.ruleId}`,
                setId: ruleSet.id,
                ruleSet,
                rule,
            }),
        ),
        {
            kind: 'set-footer',
            key: `footer:${ruleSet.id}`,
            setId: ruleSet.id,
            ruleSet,
            isLast: index === ruleSets.length - 1,
        },
    ]);
};

/** Only the set headers, in order. Shown while a whole set is being dragged. */
export const getSetHeaderRows = (rows: FlatRow[]): FlatRow[] =>
    rows.filter((row) => row.kind === 'set-header');

/** Ids of the sets in the order their headers appear in `rows`. */
export const getSetIdsInOrder = (rows: FlatRow[]): string[] =>
    rows.filter((row) => row.kind === 'set-header').map((row) => row.setId);

/**
 * Applies the order of `rows` after a goal was dragged: each goal belongs to
 * the closest set header above it, and its priority is its position among that
 * set's goals. A goal dropped above the first header snaps into the first set.
 * Rules that aren't goal rows in `rows` (bills, spend) are left untouched.
 */
export const applyReorderedGoals = (
    moneyMap: MoneyMapWithRulesAndAccounts,
    rows: FlatRow[],
): MoneyMapWithRulesAndAccounts => {
    const firstSetId = rows.find((row) => row.kind === 'set-header')?.setId;
    if (firstSetId == null) return moneyMap;

    let currentSetId = firstSetId;
    const goalCountBySet = new Map<string, number>();
    const placementByRuleId = new Map<
        string,
        { setId: string; priority: number }
    >();
    for (const row of rows) {
        if (row.kind === 'set-header') {
            currentSetId = row.setId;
        } else if (row.kind === 'goal') {
            const priority = (goalCountBySet.get(currentSetId) ?? 0) + 1;
            goalCountBySet.set(currentSetId, priority);
            placementByRuleId.set(row.rule.ruleId, {
                setId: currentSetId,
                priority,
            });
        }
    }

    const knownGroupIds = new Set(
        moneyMap.moneyMapRuleGroups.map((group) => group.id),
    );
    const groupIdForSet = (
        setId: string,
        rule: MoneyMapRuleWithAccount,
    ): string | undefined => {
        if (setId !== UNGROUPED_GOALS_SET_ID) return setId;
        // The pseudo "ungrouped" set has no real group. Keep a dangling group
        // id as is; otherwise the rule becomes ungrouped.
        const existing = rule.moneyMapRuleGroupId;
        return existing != null && !knownGroupIds.has(existing)
            ? existing
            : undefined;
    };

    return {
        ...moneyMap,
        moneyMapRules: moneyMap.moneyMapRules.map((rule) => {
            const placement = placementByRuleId.get(rule.id);
            if (placement == null) return rule;
            return {
                ...rule,
                priority: placement.priority,
                moneyMapRuleGroupId: groupIdForSet(placement.setId, rule),
            };
        }),
    };
};
